import { ByocUploadContentService } from './../../service/upload-content.service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ByocEpisodeService } from '../../service/episode.service';
import { Subscription, tap } from 'rxjs';
import { ByocEpisodeStepCourseListComponent } from './list/list.component';
import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { Router } from '@angular/router';
import { MessageService } from '@app/service/message.service';
import { TranslateService } from '@ngx-translate/core';
import { control } from '../../../utils';

enum origin {
    all = 0,
    st = 1,
    custom = 2,
}

@Component({
    selector: 'app-byoc-episode-step-course',
    templateUrl: './course.component.html',
    styleUrls: ['./course.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ByocEpisodeStepCourseComponent implements OnInit, OnDestroy {
    private changeDetectorRef = inject(ChangeDetectorRef);
    private byocEpisodeService = inject(ByocEpisodeService);
    private byocService = inject(ByocService);
    private router = inject(Router);
    private messageService = inject(MessageService);
    private translateService = inject(TranslateService);
    private readonly byocUploadContentService = inject(ByocUploadContentService);

    @ViewChild('coursesList', { static: true })
    missionsList!: ByocEpisodeStepCourseListComponent;

    /**
     * Form Controls
     */
    searchFormControl!: UntypedFormControl;
    originControl!: UntypedFormControl;
    processing = false;

    private subscriptions: Subscription = new Subscription();

    ngOnInit() {
        this.searchFormControl = new UntypedFormControl('');
        this.originControl = new UntypedFormControl(origin.all);

        this.subscriptions.add(
            this.byocEpisodeService.form.valueChanges.subscribe(() => {
                this.changeDetectorRef.detectChanges();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    cancelClick(): void {
        this.byocEpisodeService.close('btn-close');
    }

    backClick(): void {
        this.byocEpisodeService.step = 'general';
        this.changeDetectorRef.markForCheck();
    }

    nextClick(): void {
        if (this.byocEpisodeService.form.get('courseId')!.invalid) {
            return;
        }
        this.byocEpisodeService.step = 'position';
        this.changeDetectorRef.markForCheck();
    }

    saveAsDraftClick() {
        // set as draft
        this.byocEpisodeService.form.patchValue({ status: 1 });

        // show loader
        this.processing = true;
        this.changeDetectorRef.detectChanges();

        // save as draft
        this.subscriptions.add(
            this.byocService
                .createEpisode(this.byocEpisodeService.getEpisodeDataToSent())
                .pipe(
                    tap(() => {
                        if (this.byocUploadContentService.uploadType) {
                            this.byocEpisodeService.step = 'uploaded-content-info';
                            this.changeDetectorRef.markForCheck();
                            return;
                        } else if (this.router.url === '/manager/settings/content/byoc/list') {
                            this.messageService.setMessage(this.translateService.instant('v3-byoc-popup-create-episode-alert-save-as-draft'), 'info');
                            this.byocEpisodeService.close('btn-close');
                        } else {
                            this.messageService.setRouteMessage(this.translateService.instant('v3-byoc-popup-create-episode-alert-save-as-draft'), 'info');
                            this.router.navigate(['/manager', 'settings', 'content', 'byoc', 'list']);
                        }
                    }),
                )
                .subscribe(),
        );
    }

    usersClick() {
        this.byocEpisodeService.form.patchValue({ status: 2 });
        this.byocEpisodeService.step = 'users';
    }

    /**
     * Course ID Control
     */
    get courseIdControl() {
        return control(this.byocEpisodeService.form.get('courseId'));
    }

    /**
     * if selected course doesn't have episodes
     */
    get isSelectedCourseEmpty() {
        const selectedCourse = this.missionsList.courses.find((course) => course.id === this.byocEpisodeService.value['courseId']);
        return selectedCourse ? selectedCourse.stats?.episodes?.total === 0 : true;
    }

    get isCourseSelected() {
        return !!this.byocEpisodeService.value['courseId'];
    }
}
